/* eslint-disable sonarjs/no-duplicate-string */
import {
  Flex, Box, Text, Spinner,
} from '@huspy/briks-web';
import { formatNumber } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mantine/core';
import feeFinanceBreakdownStyles from './styles';

const {
  feeFinancingHeader, feeFinancingContent, feeFinancingTable, feeFinancingSeparator,
} = feeFinanceBreakdownStyles;

type Props = {
  isRealEstateFeeFinanced: boolean;
  isLandDepartmentFeeFinanced: boolean;
  loanAmount: number;
  feesAmount: {
    real_estate: number;
    land_department: number;
  };
  currency: string;
  canBeFeeFinanced?: boolean;
  isLoading?: boolean;
  landDepartmentFeeFinanceHandler: (value: boolean) => void;
  realEstateFeeFinanceHandler: (value: boolean) => void;
};

const LoadingState = () => {
  const { t } = useTranslation();
  return ((
    <Flex align='center' justify='end' gap='2'>
      <Spinner size='sm' />
      <Text size='sm' color='text-tertiary'>
        {t('proposals.proposalsDetails.feeFinancing.calculating')}
      </Text>
    </Flex>
  ));
};

const NotApplicableState = () => {
  const { t } = useTranslation();
  return ((
    <Text
      size='md'
      weight='semiBold'
      color='text-primary'
      data-testid='not-applicable-disclaimer'
    >
      {t('proposals.proposalsDetails.feeFinancing.notApplicable')}
    </Text>
  ));
};

const RenderedFees = ({ isFeeFinanced, feesAmount, currency }:
{ isFeeFinanced: boolean, feesAmount: number, currency: string }) => (
  <Text
    size='md'
    weight='semiBold'
    color={ isFeeFinanced ? 'utility-indigo-500' : 'text-primary' }
  >
    {isFeeFinanced ? (
      <>
        {currency}
        {' '}
        {formatNumber(feesAmount)}
      </>
    ) : '-'}
  </Text>
);

const FeeFinanceBreakdown = ({
  loanAmount,
  feesAmount,
  currency,
  canBeFeeFinanced,
  isLandDepartmentFeeFinanced,
  isRealEstateFeeFinanced,
  landDepartmentFeeFinanceHandler,
  realEstateFeeFinanceHandler,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();

  const renderLandDepartmentFees = () => {
    if (!canBeFeeFinanced) {
      return <NotApplicableState />;
    }
    if (isLoading) {
      return <LoadingState />;
    }
    return (
      <RenderedFees
        isFeeFinanced={ isLandDepartmentFeeFinanced }
        feesAmount={ feesAmount.land_department }
        currency={ currency }
      />
    );
  };
  const renderRealEstateFees = () => {
    if (!canBeFeeFinanced) {
      return <NotApplicableState />;
    }
    if (isLoading) {
      return <LoadingState />;
    }
    return (
      <RenderedFees
        isFeeFinanced={ isRealEstateFeeFinanced }
        feesAmount={ feesAmount.real_estate }
        currency={ currency }
      />
    );
  };

  return (
    <Flex gap={ 4 } style={ { width: '100%' } }>
      <Flex flex={ 1 } gap={ 4 }>
        <Flex alignItems='center' h='fit-content' gap='1.5'>
          <Checkbox
            display='block'
            checked={ isRealEstateFeeFinanced }
            data-testid='real-estate-fee-financed-option'
            onChange={ (e) => realEstateFeeFinanceHandler(e.target.checked) }
          />
          <Text size='sm' color='text-tertiary'>Real Estate</Text>
        </Flex>
        <Flex alignItems='center' h='fit-content' gap='1.5'>
          <Checkbox
            display='block'
            data-testid='land-department-fee-financed-option'
            checked={ isLandDepartmentFeeFinanced }
            onChange={ (e) => landDepartmentFeeFinanceHandler(e.target.checked) }
          />
          <Text size='sm' color='text-tertiary'>Land Department</Text>
        </Flex>
      </Flex>

      <Flex flex={ 1 } direction='column'>
        <Flex className={ feeFinancingHeader }>
          <Text size='lg' color='text-primary_on-brand'>
            {t('proposals.proposalsDetails.feeFinancing.loanCalculation')}
          </Text>
        </Flex>
        <Flex flex={ 1 } className={ feeFinancingContent } direction='column'>
          <table className={ feeFinancingTable }>
            <thead>
              <tr>
                <th align='left'>
                  <Text size='md' weight='regular'>
                    {t('proposals.proposalsDetails.feeFinancing.originalLoan')}
                  </Text>
                </th>
                <th align='right'>
                  <Text size='md' weight='semiBold'>
                    {currency}
                    {' '}
                    {formatNumber(loanAmount)}
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Text size='md' color={ isRealEstateFeeFinanced ? 'utility-indigo-500' : 'text-primary' }>
                    Real Estate fees
                  </Text>
                </td>
                <td align='right'>
                  {renderRealEstateFees()}
                </td>
              </tr>
              <tr>
                <td>
                  <Text size='md' color={ isLandDepartmentFeeFinanced ? 'utility-indigo-500' : 'text-primary' }>
                    Land Department fees
                  </Text>
                </td>
                <td align='right'>
                  {renderLandDepartmentFees()}
                </td>
              </tr>

              <tr>
                <td colSpan={ 2 }>
                  <Box className={ feeFinancingSeparator } />
                </td>
              </tr>

              <tr>
                <td>
                  <Text size='md'>
                    {t('proposals.proposalsDetails.feeFinancing.total')}
                  </Text>
                </td>
                <td align='right'>
                  {isLoading ? <LoadingState /> : (
                    <Text size='md' weight='semiBold'>
                      {currency}
                      {' '}
                      {formatNumber(loanAmount
                      + ((canBeFeeFinanced ? feesAmount.land_department : 0)
                      + (canBeFeeFinanced ? feesAmount.real_estate : 0)))}
                    </Text>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <Text size='sm' color='text-tertiary'>
            {t('proposals.proposalsDetails.feeFinancing.disclaimer')}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default FeeFinanceBreakdown;
